import { pushToDataLayer } from "./dataLayer.js";
import { AnalyticsEvents, AnalyticsParameters } from "./constants.js";

// Section-view impressions are once-per-page-load by definition, and a
// section can be observed from a remounted component (e.g. SPA nav away from
// and back to Home) without that counting as a new impression. Module-scoped
// so the guard survives component mount/unmount, not just one hook instance.
/** @type {Set<string>} */
const firedSections = new Set();

/**
 * The `page` + `language` enrichment shared by every marketing event. Optional
 * so a bare call still works (backward compatible); call sites on the marketing
 * site supply both from `useAnalyticsContext()`. Keys map to
 * `AnalyticsParameters`.
 *
 * @typedef {Object} PageContext
 * @property {string} [page]     Which page the interaction happened on (e.g. "home").
 * @property {string} [language] Visitor language short code (e.g. "en", "el").
 */

/**
 * Extra context carried on a CTA / lead event, on top of `location`. Extends
 * `PageContext` with the CTA-only fields `ctaName` + `plan`. Every field is
 * optional so a bare `trackCTA("x")` still works (backward compatible); call
 * sites are expected to supply `page`, `language`, and `ctaName`, plus `plan`
 * where a plan is in play. `ctaName` becomes `cta_name` on the wire.
 *
 * @typedef {Object} CtaContext
 * @property {string} [page]     Which page the CTA sits on (e.g. "home").
 * @property {string} [language] Visitor language short code (e.g. "en", "el").
 * @property {string} [ctaName]  Semantic CTA name (e.g. "register").
 * @property {string} [plan]     Plan the CTA points at, when applicable.
 */

/**
 * Build the `page` + `language` params shared by every marketing event.
 * Undefined fields are omitted so the emitted dataLayer object stays clean (a
 * missing key reads the same as an empty variable in GTM). One owner for this
 * pair means CTA, section, contact, and newsletter events enrich identically.
 *
 * @param {PageContext} [context]
 * @returns {Record<string, string>}
 */
function pageContextParams(context = {}) {
  const { page, language } = context;
  const params = {};
  if (page !== undefined) params[AnalyticsParameters.PAGE] = page;
  if (language !== undefined) params[AnalyticsParameters.LANGUAGE] = language;
  return params;
}

/**
 * Build the optional context params for a CTA/lead payload — the shared
 * `page`/`language` pair plus the CTA-only `cta_name`/`plan`. Undefined fields
 * are omitted. Keeping this in one place means `trackCTA` and `trackLead`
 * enrich identically.
 *
 * @param {CtaContext} [context]
 * @returns {Record<string, string>}
 */
function ctaContextParams(context = {}) {
  const { ctaName, plan } = context;
  const params = pageContextParams(context);
  if (ctaName !== undefined) params[AnalyticsParameters.CTA_NAME] = ctaName;
  if (plan !== undefined) params[AnalyticsParameters.PLAN] = plan;
  return params;
}

/**
 * Fire for every CTA that sends the visitor toward the app (or a subdomain
 * funnel step), regardless of destination. `location` identifies which CTA
 * on the page — see call sites for the location vocabulary in use today.
 * `context` enriches the event with page / language / cta_name / plan.
 *
 * @param {string} location
 * @param {CtaContext} [context]
 * @returns {void}
 */
export function trackCTA(location, context) {
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.CTA_CLICK,
    [AnalyticsParameters.LOCATION]: location,
    ...ctaContextParams(context),
  });
}

/**
 * Fire alongside `trackCTA` only for CTAs that represent real signup/trial
 * intent (destination is the create-event flow or the Éros trial, not e.g.
 * "sign in" for an already-registered visitor, or a sample-invitation
 * preview). Maps to GA4's recommended `generate_lead` event and whatever
 * Meta Pixel "Lead" tag is wired to it in GTM. `context` enriches the event
 * with page / language / cta_name / plan.
 *
 * @param {string} location
 * @param {CtaContext} [context]
 * @returns {void}
 */
export function trackLead(location, context) {
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.GENERATE_LEAD,
    [AnalyticsParameters.LOCATION]: location,
    ...ctaContextParams(context),
  });
}

/**
 * Generic "this section was seen" impression. Fires at most once per section
 * per page load. The mechanism is section-name-agnostic, but the supported
 * `section` values are a closed set today — `pricing`, `invitation_preview`,
 * and `eros` — each with its own named wrapper below so a call site never
 * spells the section string itself. `context` enriches the impression with
 * page / language.
 *
 * @param {string} section
 * @param {PageContext} [context]
 * @returns {void}
 */
export function trackSectionViewed(section, context) {
  if (firedSections.has(section)) return;
  firedSections.add(section);
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.SECTION_VIEW,
    [AnalyticsParameters.SECTION]: section,
    ...pageContextParams(context),
  });
}

/**
 * Pricing section seen. Thin wrapper so "pricing" has one owner.
 * @param {PageContext} [context]
 * @returns {void}
 */
export function trackPricingViewed(context) {
  trackSectionViewed("pricing", context);
}

/**
 * Invitation-preview surface seen (the "see a sample invitation" preview).
 * Thin wrapper so "invitation_preview" has one owner.
 * @param {PageContext} [context]
 * @returns {void}
 */
export function trackInvitationPreviewViewed(context) {
  trackSectionViewed("invitation_preview", context);
}

/**
 * Éros spotlight seen. Thin wrapper so "eros" has one owner.
 * @param {PageContext} [context]
 * @returns {void}
 */
export function trackErosViewed(context) {
  trackSectionViewed("eros", context);
}

/**
 * Contact page viewed. Intentionally NOT deduped across mounts — each real
 * visit to /contact is a legitimate page view. `context` enriches the event
 * with page / language.
 *
 * @param {PageContext} [context]
 * @returns {void}
 */
export function trackContact(context) {
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.CONTACT_VIEW,
    ...pageContextParams(context),
  });
}

/**
 * Contact form successfully submitted (fired only after a 2xx response).
 * `context` enriches the event with page / language.
 *
 * @param {PageContext} [context]
 * @returns {void}
 */
export function trackContactSubmitted(context) {
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.CONTACT_FORM_SUBMIT,
    ...pageContextParams(context),
  });
}

/**
 * Newsletter subscription succeeded — fire only AFTER a successful (2xx)
 * subscription response, never on submit intent. `context` enriches the event
 * with page / language.
 *
 * @param {PageContext} [context]
 * @returns {void}
 */
export function trackNewsletterSubscribed(context) {
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.NEWSLETTER_SUBSCRIBED,
    ...pageContextParams(context),
  });
}

/**
 * Extra context carried on the `signup_started` auth event. All fields optional
 * so a bare `trackSignupStarted()` still works. `eventType` becomes `event_type`
 * on the wire.
 *
 * @typedef {Object} SignupStartedContext
 * @property {string} [eventType] Celebration type (`wedding`\|`baptism`\|`wedding_baptism`).
 * @property {string} [plan]      Plan from the `?plan=` param (`free`\|`basic`\|`pro`).
 * @property {string} [ref]       Referring CTA, when present (e.g. `eros`).
 */

/**
 * The organizer began the create-event (signup) flow — Browser-owned INTENT,
 * fired once when the create-event form is first seen. This is the ONLY auth
 * event owned by the browser: the authoritative account/verification/login
 * transitions are Backend-owned and are not emittable from here (see
 * docs/analytics/event-ownership.md). De-duplication (StrictMode, remounts,
 * route-toggling across the three create-event types) is the call site's
 * responsibility — this function is a pure emitter.
 *
 * @param {SignupStartedContext} [context]
 * @returns {void}
 */
export function trackSignupStarted(context = {}) {
  const { eventType, plan, ref } = context;
  const params = {};
  if (eventType !== undefined) params[AnalyticsParameters.EVENT_TYPE] = eventType;
  if (plan !== undefined) params[AnalyticsParameters.PLAN] = plan;
  if (ref !== undefined) params[AnalyticsParameters.REF] = ref;
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.SIGNUP_STARTED,
    ...params,
  });
}

/**
 * Extra context carried on the `checkout_started` payment event. All fields
 * optional so a bare `trackCheckoutStarted()` still works. `plan` is the tier
 * being bought; `fromPlan` the tier held before it (becomes `from_plan` on the
 * wire); `source` is where checkout began.
 *
 * @typedef {Object} CheckoutStartedContext
 * @property {string} [plan]     Tier being purchased (`basic`\|`pro`).
 * @property {string} [fromPlan] Tier held before the upgrade (`free`\|`basic`).
 * @property {string} [source]   Where checkout began (`signup`\|`billing`).
 */

/**
 * The couple began a plan checkout — Browser-owned begin-checkout INTENT, fired
 * on the upgrade CTA just before redirecting to Stripe. This is NOT a conversion
 * and NOT the money truth: the authoritative `purchase` is Backend-owned (the
 * Stripe `checkout.session.completed` webhook) and is not emittable from here
 * (see docs/analytics/event-ownership.md §8). De-duplication (double-click,
 * remounts) is the call site's responsibility — this function is a pure emitter.
 *
 * @param {CheckoutStartedContext} [context]
 * @returns {void}
 */
export function trackCheckoutStarted(context = {}) {
  const { plan, fromPlan, source } = context;
  const params = {};
  if (plan !== undefined) params[AnalyticsParameters.PLAN] = plan;
  if (fromPlan !== undefined) params[AnalyticsParameters.FROM_PLAN] = fromPlan;
  if (source !== undefined) params[AnalyticsParameters.SOURCE] = source;
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.CHECKOUT_STARTED,
    ...params,
  });
}

/**
 * Extra context carried on the `upload_started` gallery event. All fields
 * optional so a bare `trackUploadStarted()` still works. `fileCount`/
 * `photoCount`/`videoCount` become `file_count`/`photo_count`/`video_count`,
 * `uploadSource` becomes `upload_source`, on the wire.
 *
 * @typedef {Object} UploadStartedContext
 * @property {number} [fileCount]    Files in the batch (pending, not yet uploaded).
 * @property {number} [photoCount]   Image files in the batch.
 * @property {number} [videoCount]   Video files in the batch.
 * @property {string} [uploadSource] Upload-window phase (`setup`\|`open`\|`no_date`).
 * @property {string} [role]         Uploader role (`guest`\|`couple`).
 */

/**
 * A guest or couple began an upload BATCH — Browser-owned INTENT, fired once when
 * the batch upload is confirmed (the "Upload all" action), before any bytes
 * transfer. NOT the storage truth: the authoritative `media_uploaded` is
 * Backend-owned (`POST /api/uploads/complete`) and is not emittable from here
 * (see docs/analytics/event-ownership.md §6). De-duplication (once per batch, not
 * per retry) is the call site's responsibility — this function is a pure emitter.
 *
 * @param {UploadStartedContext} [context]
 * @returns {void}
 */
export function trackUploadStarted(context = {}) {
  const { fileCount, photoCount, videoCount, uploadSource, role } = context;
  const params = {};
  if (fileCount !== undefined) params[AnalyticsParameters.FILE_COUNT] = fileCount;
  if (photoCount !== undefined) params[AnalyticsParameters.PHOTO_COUNT] = photoCount;
  if (videoCount !== undefined) params[AnalyticsParameters.VIDEO_COUNT] = videoCount;
  if (uploadSource !== undefined) params[AnalyticsParameters.UPLOAD_SOURCE] = uploadSource;
  if (role !== undefined) params[AnalyticsParameters.ROLE] = role;
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.UPLOAD_STARTED,
    ...params,
  });
}

/**
 * Extra context carried on the `upload_failed` gallery event. All fields optional
 * so a bare `trackUploadFailed()` still works. `mediaType` becomes `media_type`
 * on the wire.
 *
 * @typedef {Object} UploadFailedContext
 * @property {string} [reason]    Failure class (`offline`\|`server`\|`network`).
 * @property {string} [mediaType] Media type (`image`\|`video`).
 * @property {string} [role]      Uploader role (`guest`\|`couple`).
 */

/**
 * A single file's upload FAILED for a genuine reason — Browser-owned, fired per
 * file. It is the CALLER's job to fire this only for real failures: user
 * cancellation (`AbortError`) and policy stops (upload window closed, setup
 * limit) are NOT failures and must be excluded before calling. This function is a
 * pure emitter and does not classify the error itself.
 *
 * @param {UploadFailedContext} [context]
 * @returns {void}
 */
export function trackUploadFailed(context = {}) {
  const { reason, mediaType, role } = context;
  const params = {};
  if (reason !== undefined) params[AnalyticsParameters.REASON] = reason;
  if (mediaType !== undefined) params[AnalyticsParameters.MEDIA_TYPE] = mediaType;
  if (role !== undefined) params[AnalyticsParameters.ROLE] = role;
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.UPLOAD_FAILED,
    ...params,
  });
}

/**
 * Extra context carried on the `gallery_opened` event. All fields optional so a
 * bare `trackGalleryOpened()` still works. `eventId` becomes `event_id` on the
 * wire.
 *
 * @typedef {Object} GalleryOpenedContext
 * @property {string} [role]    Viewer role (`guest`\|`couple`).
 * @property {string} [eventId] The event whose gallery opened.
 * @property {string} [plan]    Plan tier, when the session carries one.
 */

/**
 * The gallery surface (couple or guest) was opened — Browser-owned engagement,
 * fired once per page session. De-duplication (StrictMode, remounts, SPA
 * back-and-forth between gallery and album) is the call site's responsibility —
 * this function is a pure emitter.
 *
 * @param {GalleryOpenedContext} [context]
 * @returns {void}
 */
export function trackGalleryOpened(context = {}) {
  const { role, eventId, plan } = context;
  const params = {};
  if (role !== undefined) params[AnalyticsParameters.ROLE] = role;
  if (eventId !== undefined) params[AnalyticsParameters.EVENT_ID] = eventId;
  if (plan !== undefined) params[AnalyticsParameters.PLAN] = plan;
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.GALLERY_OPENED,
    ...params,
  });
}

/**
 * Extra context carried on the `invitation_opened` event. All fields optional so
 * a bare `trackInvitationOpened()` still works. `eventId`/`eventType` become
 * `event_id`/`event_type` on the wire.
 *
 * @typedef {Object} InvitationOpenedContext
 * @property {string} [eventId]   The event whose invitation loaded.
 * @property {string} [eventType] Celebration type (`wedding`\|`baptism`\|`wedding_baptism`).
 * @property {string} [language]  Visitor language short code (`en`\|`el`).
 */

/**
 * The invitation SPA (invite.agapae.gr) loaded a public event and rendered the
 * invitation — Browser-owned engagement, fired once per invitation page load.
 * NOT the join truth: the authoritative `guest_joined` is Backend-owned (see
 * docs/analytics/event-ownership.md §5). De-duplication (once per page load,
 * across StrictMode's double-invoked fetch and the demo dual-language fetch) is
 * the call site's responsibility — this function is a pure emitter.
 *
 * @param {InvitationOpenedContext} [context]
 * @returns {void}
 */
export function trackInvitationOpened(context = {}) {
  const { eventId, eventType, language } = context;
  const params = {};
  if (eventId !== undefined) params[AnalyticsParameters.EVENT_ID] = eventId;
  if (eventType !== undefined) params[AnalyticsParameters.EVENT_TYPE] = eventType;
  if (language !== undefined) params[AnalyticsParameters.LANGUAGE] = language;
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.INVITATION_OPENED,
    ...params,
  });
}

/**
 * Extra context carried on the `rsvp_started` RSVP event. All fields optional so
 * a bare `trackRsvpStarted()` still works. `eventId`/`eventType` become
 * `event_id`/`event_type` on the wire.
 *
 * @typedef {Object} RsvpStartedContext
 * @property {string} [eventId]   The invitation whose RSVP flow opened.
 * @property {string} [eventType] Celebration type (`wedding`\|`baptism`\|`wedding_baptism`).
 */

/**
 * The guest opened the RSVP flow (the invitation's RSVP modal) — Browser-owned
 * INTENT, fired once per invitation page session. NOT the RSVP truth: the
 * authoritative `rsvp_submitted` / `rsvp_requested` are Backend-owned (see
 * docs/analytics/event-ownership.md §7). De-duplication (once per open across
 * React StrictMode, remounts, and close→reopen of the same invitation) is the
 * call site's responsibility — this function is a pure emitter.
 *
 * @param {RsvpStartedContext} [context]
 * @returns {void}
 */
export function trackRsvpStarted(context = {}) {
  const { eventId, eventType } = context;
  const params = {};
  if (eventId !== undefined) params[AnalyticsParameters.EVENT_ID] = eventId;
  if (eventType !== undefined) params[AnalyticsParameters.EVENT_TYPE] = eventType;
  pushToDataLayer({
    [AnalyticsParameters.EVENT]: AnalyticsEvents.RSVP_STARTED,
    ...params,
  });
}
